import React, { useState } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  User, 
  Building, 
  MessageSquare,
  Clock,
  Linkedin,
  Twitter,
  Youtube,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { ContactApi } from '../../services/api';
import './ContactForm.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    inquiryType: 'general',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const inquiryTypes = [
    { id: 'general', label: 'General Inquiry', icon: '💬' },
    { id: 'partnership', label: 'Partnership', icon: '🤝' },
    { id: 'support', label: 'Support', icon: '🛠️' },
    { id: 'feedback', label: 'Feedback', icon: '💡' }
  ];

  const faqs = [
    {
      question: 'What is QoreNova Academy?',
      answer: 'QoreNova Academy is an innovative learning platform focused on AI, manufacturing, and sustainable engineering. We provide hands-on courses, mentorship programs, and career development resources.'
    },
    {
      question: 'How quickly will I receive a response?',
      answer: 'We typically respond within 24-48 hours during business days. For urgent matters, please indicate so in your message subject.'
    },
    {
      question: 'Can I request a custom training program?',
      answer: 'Absolutely! We offer customized training solutions for teams and organizations. Use the Partnership inquiry type and describe your needs.'
    },
    {
      question: 'Is the platform available internationally?',
      answer: 'Yes! QoreNova Academy is accessible worldwide. Our courses are available online 24/7, and we support learners from all time zones.'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.trim().length < 10) newErrors.message = 'Message must be at least 10 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await ContactApi.submit(formData);
      
      if (response.success) {
        setSubmitted(true);
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            inquiryType: 'general',
            subject: '',
            message: ''
          });
          setSubmitted(false);
        }, 5000);
      } else {
        setSubmitError(response.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <Header />

      {/* Hero Section - Simple & Clean */}
      <section className="contact-hero">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={14} />
            <span>We'd Love to Hear From You</span>
          </div>
          <h1>Get in Touch</h1>
          <p>
            Have a question, feedback, or want to explore partnership opportunities? 
            We're here to help and typically respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="contact-main">
        <div className="contact-grid">
          
          {/* Contact Form */}
          <div className="contact-form-section">
            <div className="form-card">
              <h2>Send us a Message</h2>
              
              {submitted && (
                <div className="alert success">
                  <CheckCircle size={20} />
                  <div>
                    <strong>Message Sent!</strong>
                    <p>Thank you for reaching out. We'll get back to you soon.</p>
                  </div>
                </div>
              )}

              {submitError && (
                <div className="alert error">
                  <AlertCircle size={20} />
                  <div>
                    <strong>Something went wrong</strong>
                    <p>{submitError}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Inquiry Type */}
                <div className="inquiry-types">
                  {inquiryTypes.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      className={`inquiry-btn ${formData.inquiryType === type.id ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, inquiryType: type.id }))}
                    >
                      <span className="inquiry-icon">{type.icon}</span>
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>

                {/* Form Fields */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      <User size={16} />
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <span className="error-text">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <Mail size={16} />
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">
                      <Building size={16} />
                      Company <span className="optional">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your company"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <Phone size={16} />
                      Phone <span className="optional">(optional)</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    <MessageSquare size={16} />
                    Subject <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What's this about?"
                    className={errors.subject ? 'error' : ''}
                  />
                  {errors.subject && <span className="error-text">{errors.subject}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">
                    Message <span className="required">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your inquiry..."
                    rows={5}
                    className={errors.message ? 'error' : ''}
                  />
                  {errors.message && <span className="error-text">{errors.message}</span>}
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="contact-info-section">
            
            {/* Quick Contact */}
            <div className="info-card">
              <h3>Quick Contact</h3>
              <div className="contact-methods">
                <a href="mailto:hello@qorenova.org" className="contact-method">
                  <div className="method-icon">
                    <Mail size={20} />
                  </div>
                  <div className="method-details">
                    <span className="method-label">Email</span>
                    <span className="method-value">hello@qorenova.org</span>
                  </div>
                </a>
                
                <div className="contact-method">
                  <div className="method-icon">
                    <Clock size={20} />
                  </div>
                  <div className="method-details">
                    <span className="method-label">Response Time</span>
                    <span className="method-value">Within 24-48 hours</span>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="method-icon">
                    <MapPin size={20} />
                  </div>
                  <div className="method-details">
                    <span className="method-label">Location</span>
                    <span className="method-value">Remote-first, Global</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="info-card">
              <h3>Connect With Us</h3>
              <div className="social-links">
                <a href="https://linkedin.com/company/qorenova" target="_blank" rel="noopener noreferrer" className="social-link linkedin">
                  <Linkedin size={20} />
                  <span>LinkedIn</span>
                </a>
                <a href="https://twitter.com/qorenova" target="_blank" rel="noopener noreferrer" className="social-link twitter">
                  <Twitter size={20} />
                  <span>Twitter/X</span>
                </a>
                <a href="https://youtube.com/@qorenova" target="_blank" rel="noopener noreferrer" className="social-link youtube">
                  <Youtube size={20} />
                  <span>YouTube</span>
                </a>
              </div>
            </div>

            {/* Platform Status */}
            <div className="info-card status-card">
              <div className="status-badge">
                <span className="status-dot"></span>
                <span>Platform in Beta</span>
              </div>
              <p>
                We're actively building and improving QoreNova Academy. 
                Your feedback helps shape the future of our platform!
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${expandedFAQ === index ? 'expanded' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  {expandedFAQ === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {expandedFAQ === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Simple CTA */}
        <section className="cta-section">
          <div className="cta-content">
            <h2>Ready to Start Learning?</h2>
            <p>Explore our courses and begin your journey in AI, manufacturing, and engineering.</p>
            <a href="/academy/courses" className="cta-btn">
              Browse Courses
              <ArrowRight size={18} />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
